import { ReactNode, useState } from "react";
import { Colors, Icon } from "main";

type BaseAccordionProps = {
  title: string;
  children: ReactNode;
  // TODO: Once the <Chip/> component is available in the shared library,
  // stop accepting a custom chip via this prop. Update the prop and its
  // name as needed to properly integrate with <Chip/>.
  chip?: ReactNode;

  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
};

type AccordionProps =
  | (BaseAccordionProps & { isOpen?: never; onToggle?: never }) // Uncontrolled mode
  | (BaseAccordionProps & { isOpen: boolean; onToggle: () => void }); // Controlled mode

/**
 * Component | Accordion
 *
 * A collapsible section with the appropriate styles that renders its children.
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=13546-15496&m
 */
export function Accordion(props: AccordionProps) {
  const {
    title,
    chip,
    children,
    isOpen: controlledIsOpen,
    onToggle,
    titleStyle,
    style,
    headerStyle,
  } = props;

  const [internalIsOpen, setInternalIsOpen] = useState(controlledIsOpen ?? false);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  function handleToggle() {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalIsOpen((prev) => !prev);
    }
  }

  return (
    <>
      <div
        style={{
          padding: "16px 0",
          backgroundColor: Colors.white200,
          borderBottom: `1px solid ${Colors.slate300}`,
          ...style,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "8px 16px",
            display: "flex",
            justifyContent: "space-between",
            ...headerStyle,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                lineHeight: "24px",
                letterSpacing: "0.15px",
                fontSize: 20,
                ...titleStyle,
              }}
            >
              {title}
            </span>
            {/* TODO: Replace with <Chip/> from this project once it's available,
            instead of using a prop. */}
            {chip && chip}
          </div>
          <button
            style={{ background: "none" }}
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Collapse section" : "Expand section"}
          >
            <Icon name={isOpen ? "Chevron Up" : "Chevron Down"} />
          </button>
        </div>
      </div>
      {/* Collapsible Content */}
      {isOpen && children}
    </>
  );
}
